/**
 * Разблокировка приложения БЕЗ СЕТИ (PIN и Face ID).
 *
 * PIN-экран — чисто клиентская блокировка (сервер состояние «разблокировано» не
 * хранит), но проверка PIN и Face ID шла только через API. После перезапуска
 * приложения (iOS выгружает PWA постоянно) замерщик без интернета упирался в
 * экран PIN и не мог работать вообще.
 *
 * Поэтому после каждой УСПЕШНОЙ онлайн-проверки запоминаем локальный проверочный
 * материал, а без сети сверяемся с ним:
 *  - PIN: PBKDF2-хеш с солью (сам PIN не храним); 5 неверных попыток подряд —
 *    офлайн-вход блокируется до первой онлайн-проверки;
 *  - Face ID: id ключей пользователя и rpId. Без сети просим у устройства подпись
 *    локального challenge с userVerification='required' — успешный ответ значит,
 *    что биометрия на этом устройстве пройдена для ключа этого сотрудника.
 *
 * Всё привязано к userId: другой сотрудник на том же телефоне этим не воспользуется.
 * При выходе из аккаунта материалы стираются (forgetOfflineUnlock).
 */
import { startAuthentication } from '@simplewebauthn/browser';
import { HttpError } from './api.js';

type AuthOptions = Parameters<typeof startAuthentication>[0]['optionsJSON'];

const PIN_KEY = 'jb:offline_pin';
const BIO_KEY = 'jb:offline_bio';
const MAX_FAILURES = 5;
const ITERATIONS = 150_000;

interface PinRecord { userId: string; salt: string; hash: string; failures: number }
interface BioRecord { userId: string; rpId?: string; allowCredentials: NonNullable<AuthOptions['allowCredentials']> }

/** Сервер недоступен: нет сети, таймаут или прокси отвечает 502/503/504 (API перезапускается). */
export function isServerUnreachable(e: unknown): boolean {
  if (e instanceof HttpError) return e.status >= 502 && e.status <= 504;
  return true;
}

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* хранилище недоступно — офлайн-входа не будет */ }
}

function toB64(bytes: ArrayBuffer | Uint8Array): string {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = '';
  arr.forEach((b) => { s += String.fromCharCode(b); });
  return btoa(s);
}

function fromB64(value: string): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(atob(value), (c) => c.charCodeAt(0));
}

function toB64Url(bytes: Uint8Array): string {
  return toB64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function derive(pin: string, salt: Uint8Array<ArrayBuffer>): Promise<string> {
  const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations: ITERATIONS }, key, 256);
  return toB64(bits);
}

function cryptoAvailable(): boolean {
  return typeof crypto !== 'undefined' && !!crypto.subtle;
}

/** Запомнить PIN после успешной онлайн-проверки или установки. */
export async function rememberPin(userId: string, pin: string): Promise<void> {
  if (!cryptoAvailable()) return;
  try {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    write(PIN_KEY, { userId, salt: toB64(salt), hash: await derive(pin, salt), failures: 0 } satisfies PinRecord);
  } catch { /* без офлайн-PIN приложение работает как раньше */ }
}

export function forgetPin() {
  try { localStorage.removeItem(PIN_KEY); } catch { /* ignore */ }
}

export type OfflinePinResult = 'ok' | 'wrong' | 'unavailable';

/** Проверить PIN без сервера. 'unavailable' — на этом устройстве ещё не было онлайн-входа по PIN или попытки исчерпаны. */
export async function verifyPinOffline(userId: string, pin: string): Promise<OfflinePinResult> {
  const record = read<PinRecord>(PIN_KEY);
  if (!record || record.userId !== userId || !cryptoAvailable()) return 'unavailable';
  if (record.failures >= MAX_FAILURES) return 'unavailable';
  const hash = await derive(pin, fromB64(record.salt));
  if (hash === record.hash) {
    if (record.failures) write(PIN_KEY, { ...record, failures: 0 });
    return 'ok';
  }
  write(PIN_KEY, { ...record, failures: record.failures + 1 });
  return record.failures + 1 >= MAX_FAILURES ? 'unavailable' : 'wrong';
}

/** Запомнить ключи Face ID после успешной онлайн-разблокировки. */
export function rememberBiometric(userId: string, options: AuthOptions) {
  if (!options.allowCredentials?.length) return;
  write(BIO_KEY, { userId, rpId: options.rpId, allowCredentials: options.allowCredentials } satisfies BioRecord);
}

export function forgetBiometric() {
  try { localStorage.removeItem(BIO_KEY); } catch { /* ignore */ }
}

export function hasOfflineBiometric(userId: string | undefined): boolean {
  const record = read<BioRecord>(BIO_KEY);
  return !!userId && record?.userId === userId;
}

/** Face ID без сервера. true — биометрия пройдена, false — отмена или нет сохранённых ключей. */
export async function unlockBiometricOffline(userId: string): Promise<boolean> {
  const record = read<BioRecord>(BIO_KEY);
  if (!record || record.userId !== userId) return false;
  try {
    await startAuthentication({
      optionsJSON: {
        challenge: toB64Url(crypto.getRandomValues(new Uint8Array(32))),
        rpId: record.rpId,
        allowCredentials: record.allowCredentials,
        userVerification: 'required',
        timeout: 60_000,
      },
    });
    return true;
  } catch (e) {
    const name = (e as { name?: string })?.name;
    if (name === 'NotAllowedError' || name === 'AbortError') return false;
    throw e;
  }
}

export function forgetOfflineUnlock() {
  forgetPin();
  forgetBiometric();
}
